// 7. 10. 2026 (owner, Fuel for every build): Fuel was found everywhere and burnt by the Chainsaw alone. Now the truck takes
// a survivor to a zone and back (round 2, 7. 10.: with more room for the haul, no longer a shorter run), the generator at
// Station 9 runs the Workshop faster, and fire barrels lit before the evening horde burn it in every wave. The Molotov is
// a Workshop recipe (supplies.ts `MOLOTOV`).

import type { StackMap, ZoneId } from './types';

/**
 * The truck: a run of `minMinutes` or longer has `capacity` more pack room for the zone's Fuel × `fuelMult` (owner,
 * 7. 10. 2026, round 2; before, the run was 20% shorter: the active players reached L20 1–2 days early). The run lasts as
 * picked. 9. 10. 2026 (owner, after round 2: +30% for the table's Fuel gave the active players 2.2–2.4× the Fuel they
 * used and 6–9% more loot a day): +20% for ×1.4 the Fuel.
 */
export const TRUCK_RULES = {
  capacity: 0.2,
  fuelMult: 1.4,
  minMinutes: 20,
  /** Fuel for the trip there and back before `fuelMult`: the nearer zones 3–4, the far ones (Mountains, Wasteland, the secret zones) 12. */
  fuel: {
    forest: 3,
    gas_station: 3,
    downtown: 4,
    hospital: 6,
    factory: 6,
    military_base: 8,
    mountains: 12,
    wasteland: 12,
    secret_bunker: 12,
    underground_lab: 12,
    survivor_vault: 12,
  } as Record<ZoneId, number>,
};

/** The truck's Fuel to a zone (the table × `fuelMult`, rounded). */
export function truckFuel(zone: ZoneId): number {
  return Math.round((TRUCK_RULES.fuel[zone] ?? 12) * TRUCK_RULES.fuelMult);
}

/** Whether a run of this base duration can take the truck. */
export function truckAllowed(baseMinutes: number): boolean {
  return baseMinutes >= TRUCK_RULES.minMinutes;
}

/**
 * The generator at Station 9: while it is on, the first thing a survivor does on a day pays the day's Fuel from Storage,
 * and that day the Workshop crafts `craftSpeed` faster (on top of the other bonuses, within their cap). Without the Fuel
 * it stands still that day: no bonus, nothing breaks.
 */
export const GENERATOR = { fuelPerDay: 6, craftSpeed: 0.15, unlockLevel: 6 };

export interface GeneratorState {
  on: boolean;
  /** The last day it was paid for (the player's day, `playerToday`). */
  paidDay?: string;
}

/** Whether the generator runs today (paid for this day). */
export function generatorRunning(g: GeneratorState | null | undefined, today: string): boolean {
  return !!g?.on && g.paidDay === today;
}

/** The day's Fuel, the first time a day: what Storage and the state become, and whether it runs now. */
export function generatorDay(g: GeneratorState | null | undefined, storage: StackMap, today: string): { gen: GeneratorState | null | undefined; storage: StackMap; running: boolean; paid: boolean } {
  if (!g?.on) return { gen: g, storage, running: false, paid: false };
  if (g.paidDay === today) return { gen: g, storage, running: true, paid: false };
  const fuel = storage.fuel ?? 0;
  if (fuel < GENERATOR.fuelPerDay) return { gen: g, storage, running: false, paid: false };
  const left = fuel - GENERATOR.fuelPerDay;
  const next: StackMap = { ...storage };
  if (left > 0) next.fuel = left;
  else delete next.fuel;
  return { gen: { ...g, paidDay: today }, storage: next, running: true, paid: true };
}

/**
 * Fire barrels: lit in the hour before the evening horde (`windowMinutes`), once a horde, for `fuel` Fuel. In every wave
 * the survivor fights that evening the horde burns at the start of each round, `pctOfAttack`% of the survivor's attack.
 */
export const FIRE_BARRELS = { fuel: 15, pctOfAttack: 15, windowMinutes: 60 };

/** Whether the barrels can be lit now: the hour before the horde starts. */
export function barrelsOpen(startsAt: number, now: number): boolean {
  return now >= startsAt - FIRE_BARRELS.windowMinutes * 60_000 && now < startsAt;
}

/**
 * 10. 10. 2026 (owner, BACKLOG #41): the horde is prepared like a run. `bandages`: suggested for a wave; `healLine`: a
 * Bandage (or a Medkit) from the pack between the wave's fights below this share of health.
 */
export const HORDE_PREP = { bandages: 2, healLine: 0.5 };

/** (The rounds or Fuel a wave takes: `hordeWaveFeed`, items/supplies.ts; × the waves for Stand guard.) */

/** 10. 10. 2026: what a survivor set aside for tonight's guard waves at the payment (the horde's event). */
export interface GuardPack {
  eventId: string;
  kind: 'ammo' | 'fuel' | null;
  feed: number;
  bandage: number;
}
